import React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import type { ActionProps, ImageSizesProps, ImageSourcesProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { QuoteBlockClassNames } from './quoteBlock.state'

export const quoteBlockClasses: QuoteBlockClassNames = {
  // DEFAULTS
  block: 'block block--quote',
  inview: 'inview',
  inner: 'block__inner ls-container--l ls-gutter ls-spacing--s',
  content: 'block__content',
  text: 'quote__text quote--m',
  visual: 'block__visual',
  image: 'block__image',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--secondary',

  // BLOCK SPECIFIC
  innerNew: 'block__inner ls-container ls-gutter ls-spacing--s',
  blockText: 'block--quote-text',
  blockImage: 'block--quote-image',
  defaultLayoutText: 'quote quote--text',
  newLayoutText: 'quote-new quote--text',
  defaultLayoutImage: 'quote quote--image',
  newLayoutImage: 'quote-new quote--image',
  quote: 'quote__block',
  caption: 'quote__caption',
  author: 'quote__author',
  authorDescription: 'quote__extra',
}

export const QuoteBlockLayout = {
  Inner: (props: {
    children?: React.ReactNode
    hasImage: boolean
    amountNested: number
    design: 'default' | 'new'
  }): JSX.Element => {
    const { children, hasImage, design } = props
    return (
      <div className={getClassNameModern(quoteBlockClasses, design === 'default' ? 'inner' : 'innerNew', '')}>
        <div
          className={getClassNameModern(
            quoteBlockClasses,
            design === 'default' && hasImage
              ? 'defaultLayoutImage'
              : design === 'new' && hasImage
              ? 'newLayoutImage'
              : design === 'default'
              ? 'defaultLayoutText'
              : 'newLayoutText',
            `${hasImage ? 'row align-items-center' : ''}`
          )}
        >
          {children}
        </div>
      </div>
    )
  },
  DefaultContent: (
    props: {
      hasImage: boolean
      text: string
      author?: string
      authorDescription?: string
      showActionAsLink: boolean
    } & ActionProps
  ) => {
    const {
      hasImage,
      text,
      author,
      authorDescription,
      showPrimary,
      showSecondary,
      primaryAction,
      secondaryAction,
      showActionAsLink,
    } = props
    return (
      <>
        <div className={getClassNameModern(quoteBlockClasses, 'content', hasImage ? 'col-12 col-md-9 col-xl-10' : '')}>
          <SharedBlockLayout.SVGIcon
            iconId="#icon--quote"
            width="99px"
            height="81px"
            className="quote__icon quote__icon--top"
          />

          <div className="quote__inner">
            <blockquote className={getClassNameModern(quoteBlockClasses, 'quote', '')}>
              <div
                className={getClassNameModern(quoteBlockClasses, 'text', 'h2 text-secondary')}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            </blockquote>
            {(author || authorDescription) && (
              <div
                className={getClassNameModern(
                  quoteBlockClasses,
                  'caption',
                  'figure-caption text-primary text-uppercase'
                )}
              >
                {author && <span className={getClassNameModern(quoteBlockClasses, 'author', 'd-block')}>{author}</span>}
                {authorDescription && (
                  <span className={getClassNameModern(quoteBlockClasses, 'authorDescription', '')}>
                    {authorDescription}
                  </span>
                )}
              </div>
            )}

            {(showPrimary || showSecondary) && (
              <footer className={getClassNameModern(quoteBlockClasses, 'footer', 'pt-5')}>
                {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
                  <div
                    className={getClassNameModern(quoteBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}
                  >
                    <SharedBlockLayout.Clickable
                      id="quotePrimaryAction"
                      type="button"
                      link={primaryAction}
                      classNames={`${getClassNameModern(
                        quoteBlockClasses,
                        showActionAsLink === true ? 'clickableSecondary' : 'clickablePrimary',
                        'btn btn-primary'
                      )} ${hasImage ? 'button--on-red' : 'button--on-darkred'}`}
                    />
                  </div>
                )}
                {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
                  <div
                    className={getClassNameModern(
                      quoteBlockClasses,
                      'clickableWrapper',
                      'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                    )}
                  >
                    <SharedBlockLayout.Clickable
                      id="quoteSecondaryAction"
                      type="button"
                      link={secondaryAction}
                      classNames={`${getClassNameModern(
                        quoteBlockClasses,
                        'clickableSecondary',
                        'btn btn-outline-primary'
                      )} ${hasImage ? 'button--on-red' : 'button--on-darkred'}`}
                    />
                  </div>
                )}
              </footer>
            )}
          </div>

          <SharedBlockLayout.SVGIcon
            iconId="#icon--quote"
            width="99px"
            height="81px"
            className="quote__icon quote__icon--bottom"
          />
        </div>
      </>
    )
  },
  DefaultVisual: (props: {
    blockIndex: string
    hasImage: boolean
    image?: ImageSizesProps['image']
    imageSources?: ImageSourcesProps['sources']
  }) => {
    const { blockIndex, hasImage, image, imageSources } = props
    return (
      <>
        {hasImage && image && imageSources && (
          <div
            className={getClassNameModern(
              quoteBlockClasses,
              'visual',
              'text-center col-12 col-md-3 col-xl-2 position-relative'
            )}
          >
            <div className="block__visual-inner fit-object">
              <SharedBlockLayout.ModernImagePicture
                pictureKey={`quote-${blockIndex}`}
                image={image}
                sources={imageSources}
                lazyload={true}
                priority={undefined}
                classNames={getClassNameModern(
                  quoteBlockClasses,
                  'image',
                  'rounded-circle d-inline-block mb-3 mb-md-auto'
                )}
                pictureClassNames="block__picture"
                imageProportions={{
                  width: '170',
                  height: '170',
                  sizes: '170px',
                }}
              />
            </div>
            <svg
              className="block__mask block__mask--desk"
              height="356"
              viewBox="0 0 475 356"
              width="475"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                clipRule="evenodd"
                d="m475 347.32c-23.089 4.223-46.847 6.425-71.101 6.425-202.201 0-369.9931-153.059-401.89868-353.745h-2.00032v356h475z"
                fillRule="evenodd"
              />
            </svg>
            <svg
              className="block__mask block__mask--mob"
              height="210"
              viewBox="0 0 210 210"
              width="210"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <g>
                <path d="m105.093 189.008c-13.0135.002-25.8504-3.005-37.5032-8.785-11.6529-5.781-21.8047-14.177-29.6588-24.53s-13.1968-22.382-15.6087-35.141c-2.4119-12.76-1.8275-25.904 1.7074-38.4005l-19.93112-6.0255c-5.16591 17.9045-5.45366 36.86-.83357 54.912s13.98089 34.549 27.11779 47.79c13.1368 13.241 29.5746 22.747 47.6193 27.539s37.0439 4.697 55.0389-.277l-5.31-20.05c-7.375 2.013-14.992 3.012-22.638 2.968z" />
                <path d="m104.84.00001269c-8.8284-.00111959-17.6217 1.09653731-26.175 3.26728731l5.0077 20.1977c14.3939-3.6979 29.5153-3.5581 43.8373.4053s27.339 11.6101 37.736 22.1684l14.559-14.9403c-9.8-9.8574-21.473-17.6825-34.342-23.02105-12.869-5.33854-26.678-8.0841604-40.623-8.07733731z" />
              </g>
            </svg>
          </div>
        )}
      </>
    )
  },
  LayoutNewDesign: (props: {
    hasImage: boolean
    text: string
    author?: string
    authorDescription?: string
    blockIndex: string
    image?: ImageSizesProps['image']
    imageSources?: ImageSourcesProps['sources']
    primaryAction?: ActionProps['primaryAction']
    showPrimary?: boolean
    showActionAsLink?: boolean
  }) => {
    const {
      hasImage,
      text,
      author,
      authorDescription,
      blockIndex,
      image,
      imageSources,
      primaryAction,
      showPrimary,
      showActionAsLink,
    } = props
    return (
      <>
        <div className={getClassNameModern(quoteBlockClasses, 'content', hasImage ? 'col-12 col-md-9 col-xl-10' : '')}>
          {hasImage && image && imageSources ? (
            <QuoteBlockLayout.DefaultVisual
              blockIndex={`${blockIndex}`}
              hasImage={true}
              image={image}
              imageSources={imageSources}
            />
          ) : (
            <SharedBlockLayout.SVGIcon
              iconId="#icon--quote"
              width="99px"
              height="81px"
              className="quote__icon quote__icon--top"
            />
          )}

          <div className="quote__inner">
            <blockquote className={getClassNameModern(quoteBlockClasses, 'quote', '')}>
              <div
                className={getClassNameModern(quoteBlockClasses, 'text', 'h2 text-secondary')}
                dangerouslySetInnerHTML={{ __html: text }}
              />
            </blockquote>
            {(author || authorDescription) && (
              <div
                className={getClassNameModern(
                  quoteBlockClasses,
                  'caption',
                  'figure-caption text-primary text-uppercase'
                )}
              >
                {author && <span className={getClassNameModern(quoteBlockClasses, 'author', 'd-block')}>{author}</span>}
                {authorDescription && (
                  <span className={getClassNameModern(quoteBlockClasses, 'authorDescription', '')}>
                    {authorDescription}
                  </span>
                )}
              </div>
            )}

            {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
              <footer className={getClassNameModern(quoteBlockClasses, 'footer', 'pt-5')}>
                <div className={getClassNameModern(quoteBlockClasses, 'clickableWrapper', 'd-block')}>
                  <SharedBlockLayout.Clickable
                    id="quotePrimaryActionNew"
                    type="button"
                    link={primaryAction}
                    classNames={`${getClassNameModern(
                      quoteBlockClasses,
                      showActionAsLink ? 'clickableSecondary' : 'clickablePrimary',
                      'block__button button button--primary button--on-darkred'
                    )}`}
                  />
                </div>
              </footer>
            )}
          </div>
        </div>
      </>
    )
  },
}
